import { FormControlSizes, type Quote, type QuoteMarket, type SideEnum } from '@talos/kyoko';

import { RFQSweepingButton } from '../../../QuoteSweeping/RFQSweepingButton';
import { PartialFillButton } from './PartialFillButton';

interface ConfirmTradeParams {
  side: SideEnum;
  price: string;
  orderQty: string;
}

interface QuoteMarketPartialFillOrSweepButtonProps {
  /** Parent quote for the quote-market action. SweepGroupID decides whether this is a single-market fill or sweep. */
  quote: Quote;
  /** Specific quote market row that rendered this action. Used by the single-market partial-fill path. */
  quoteMarket: QuoteMarket;
  /** Button size used by the host surface. RFQ form and Quotes V2 use different control sizes. */
  size?: FormControlSizes;
  /** Disables buy-side trading for this action, usually from side locks or quote tradeability. */
  buyDisabled?: boolean;
  /** Disables sell-side trading for this action, usually from side locks or quote tradeability. */
  sellDisabled?: boolean;
  /** Single-market partial-fill confirmation handler. Sweep-group quotes execute through RFQSweepingButton instead. */
  onConfirmTrade: (params: ConfirmTradeParams) => void;
}

/**
 * Renders the right quote-market fill action for the quote context.
 *
 * Normal partial fills are scoped to the current quote market, but sweep-group quotes need
 * the full RFQ sweeper so already executed markets in the group are included in remaining
 * quantity validation and execution context.
 */
export function QuoteMarketPartialFillOrSweepButton({
  quote,
  quoteMarket,
  size = FormControlSizes.Xxs,
  buyDisabled,
  sellDisabled,
  onConfirmTrade,
}: QuoteMarketPartialFillOrSweepButtonProps) {
  if (quote.SweepGroupID) {
    return (
      <RFQSweepingButton
        correlationID={quote.SweepGroupID}
        disabled={buyDisabled && sellDisabled}
        size={size}
        onlyIcon
        data-testid={`quote-market-${quoteMarket.MarketAccount}-partial-fill-button`}
      >
        Sweep
      </RFQSweepingButton>
    );
  }

  return (
    <PartialFillButton
      quote={quote}
      quoteMarket={quoteMarket}
      size={size}
      buyDisabled={buyDisabled}
      sellDisabled={sellDisabled}
      onConfirmTrade={onConfirmTrade}
    />
  );
}
